"use client";
/**
 * Rapporter / Reports — Svendetid marketing feature page.
 *
 * Recreated from the Claude Design handoff "Rapporter.dc.html" and adapted onto
 * the svendetid-ds design system (shared with /features, /pricing and the other
 * feature pages). Language-parametrized so /features/reports (da) and
 * /en/features/reports (en) share one source.
 *
 * The distinctive bits carried over from the design: a live dashboard mockup in
 * the hero, a "without / with" comparison, three action-oriented feature blocks
 * with mini mockups, a dark AI block, a feature grid, a platform grid and a FAQ.
 */
import { useState } from "react";
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const T = {
  da: {
    eyebrow: "Rapporter i Svendetid",
    h1a: "Byg rapporter du faktisk ",
    h1b: "kan arbejde",
    h1c: " ud fra",
    sub: "Se alt på ét sted — omsætning, dækningsbidrag per job og fakturerbare timer. Og opdatér arbejdet direkte fra rapporten, uden at skifte mellem programmer.",
    cta: "Kom i gang – det er gratis",
    seePricing: "Se priser",
  
    trust2: "Gratis at komme i gang",
    trust3: "Dansk support",
    // dashboard mockup
    mockReport: "Rapport",
    mockTitle: "Forretningsoverblik",
    mockPeriod: "Denne måned",
    mockShare: "Del",
    kpis: [
      { label: "Omsætning", value: "kr 284.500", note: "▲ 12% vs. sidste md.", noteColor: "var(--brand)" },
      { label: "Dækningsbidrag", value: "38 %", note: "▲ 4 pct.point", noteColor: "var(--brand)" },
      { label: "Fakturerbare timer", value: "412 t", note: "86% af registreret", noteColor: "var(--text-muted)" },
      { label: "Udestående", value: "kr 47.200", note: "3 fakturaer forfaldne", noteColor: "var(--accent-strong)", valueColor: "var(--danger)" },
    ],
    chartTitle: "Omsætning per uge",
    chartUnit: "kr · 8 uger",
    donutTitle: "Jobs per status",
    donutCenter: "jobs",
    statuses: [
      { label: "I gang", count: "10", color: "#2056C0" },
      { label: "Afventer", count: "4", color: "#A57423" },
      { label: "Til fakturering", count: "5", color: "var(--accent)" },
      { label: "Færdig", count: "5", color: "var(--brand)" },
    ],
    recentTitle: "Seneste jobs",
    seeAll: "Se alle",
    rows: [
      { initial: "N", bg: "var(--tint-peach)", color: "var(--accent-ink)", title: "Tagrenovering", sub: "Nordby Bolig", status: "I gang", statusBg: "var(--tint-mint)", statusColor: "var(--brand)", amount: "kr 64.200" },
      { initial: "K", bg: "var(--tint-lilac)", color: "#4B3F8F", title: "Varmepumpe", sub: "Fam. Kruse", status: "Til fakturering", statusBg: "var(--orange-100)", statusColor: "var(--accent-strong)", amount: "kr 38.900" },
      { initial: "L", bg: "var(--tint-rose)", color: "var(--danger)", title: "Badrenovering", sub: "Fam. Lund", status: "Forsinket", statusBg: "var(--tint-rose)", statusColor: "var(--danger)", amount: "kr 88.500" },
    ],
    // without / with
    cmpH2: "Rapporter du arbejder ud fra — ikke bare kigger på",
    withoutLabel: "Uden Svendetid",
    withLabel: "Med Svendetid",
    without: [
      "Tal i regneark der aldrig passer med virkeligheden",
      "Hop mellem programmer bare for at rette én status",
      "Data spredt over sedler, regneark og apps",
      "Kunder og bogholder ringer for at høre hvor I står",
    ],
    with: [
      "Altid opdateret. Altid synkroniseret. Ingen manuel optælling",
      "Skift status, omfordel eller afslut et job direkte fra rapporten",
      "Alle tal samlet ét sted — slut med eksport til regneark",
      "Del ét link i stedet for at sende ugentlige statusmails",
    ],
    // section intro
    introLabel: "Sådan virker det",
    introH2: "Rapportering der virker som dit sjak gør",
    introSub: "Handlingsorienteret. Fleksibel. Delbar.",
    // three feature blocks
    blocks: [
      {
        label: "Handlingsorienteret",
        h3: "Se problemet. Ret det. Samme skærm.",
        body: "Skift status, omfordel job og opdatér felter direkte i rapporten. Når et job går i stå, retter du det med det samme — ingen nye faner, ingen programskift. Rapporten er altid i sync, fordi den er arbejdet.",
        chips: [
          { icon: "swap-horizontal-outline", text: "Skift status" },
          { icon: "people-outline", text: "Omfordel" },
          { icon: "create-outline", text: "Opdatér felter" },
        ],
        mock: "edit",
      },
      {
        label: "Fleksibel",
        h3: "Én rapport per kunde, projekt eller sjak",
        body: "Bland grafer, tabeller, timer og beregninger på ét lærred. Træk, tilpas og arrangér så det passer til måden I arbejder på. Byg sjak-rapporter, projektoverblik eller kundeportaler — præcis det view, hver enkelt har brug for.",
        chips: [
          { icon: "bar-chart-outline", text: "Grafer" },
          { icon: "time-outline", text: "Timer" },
          { icon: "calculator-outline", text: "Beregninger" },
        ],
        mock: "flexible",
      },
      {
        label: "Delbar",
        h3: "Del rapporter uden at give adgang til alt",
        body: "Giv kunder, bogholder og partnere et live-overblik med præcise rettigheder. De ser kun det, de skal — intet andet. Ét link erstatter alle de statusmails og PDF'er, du nogensinde har sendt.",
        chips: [
          { icon: "lock-closed", text: "Rettigheder" },
          { icon: "link-outline", text: "Ét link" },
        ],
        mock: "share",
      },
    ],
    // mock labels
    mockJobLabel: "Job · Tagrenovering",
    mockStatus: "Status",
    mockResponsible: "Ansvarlig",
    mockAmount: "Beløb",
    mockSaved: "Gemt",
    mockFrom: "I gang",
    mockTo: "Til fakturering",
    mockHoursTitle: "Timer per sjak · denne uge",
    mockMaterials: "Materialer",
    mockMargin: "Margin",
    sharePortal: "Delt kundeportal",
    shareLive: "Live",
    shareCustomer: "Anders (kunde)",
    shareCustomerRole: "Kan se",
    shareBookkeeper: "Bogholder",
    shareBookkeeperRole: "Kun økonomi",
    shareHidden: "Interne priser & sjak",
    shareHiddenRole: "Skjult",
    // AI block
    aiLabel: "AI-drevet effektivitet",
    aiH2a: "Svendetid AI gør data ",
    aiH2b: "til beslutninger",
    aiBody: "Spørg om hvad som helst: »Hvilke jobs er i fare for at give underskud?« eller »Hvor mange timer mangler vi at fakturere?«. AI'en skanner alle dine rapporter, finder mønstre og forklarer det i et sprog, du kan sende videre.",
    aiCta: "Prøv Svend AI",
    aiQuestion: "Hvilke jobs er i fare for at give underskud denne måned?",
    aiAnswerIntro: "2 jobs ligger under din målmargin:",
    aiRow1: "Badrenovering · Fam. Lund",
    aiRow1Val: "−6 %",
    aiRow2: "Carport · Holm Byg",
    aiRow2Val: "+3 %",
    aiAnswerOutro: "Materialeforbruget på Lund er 18% over tilbuddet. Vil du justere?",
    // feature grid
    gridH2: "Plus alt det, du skal bruge for at styre forretningen",
    grid: [
      { icon: "create-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Handlingsorienterede rapporter", body: "Klik ind på et kort for at omfordele, opdatere eller afslutte et job. Rapporten er ikke kun tal — den er din kommandocentral." },
      { icon: "people-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Overblik over alle sjak", body: "Se hvert sjaks jobs, arbejdsbyrde og status på én skærm. Slut med at ringe rundt for at høre hvor I står." },
      { icon: "layers-outline", iconBg: "var(--info-soft)", iconColor: "#2056C0", title: "Færdige skabeloner", body: "Start med skabeloner til projektopfølgning, kunderapport eller arbejdsbyrde. Tilpas derfra." },
      { icon: "funnel-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Avanceret filtrering", body: "Skær dine tal efter ansvarlig, status, dato eller et hvilket som helst felt. Byg præcis det view, hver person har brug for." },
      { icon: "share-social-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Delbare kundeportaler", body: "Udskift PDF'er med levende rapporter. Kunden ser fremdrift. Dit interne arbejde forbliver privat." },
      { icon: "pulse-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Live data i realtid", body: "Altid live. Altid opdateret. Ledelsen ser rigtige tal — ikke sidste uges eksport." },
    ],
    // platform
    platLabel: "Svendetid-platformen",
    platH2: "Rapporter er kun begyndelsen",
    platSub: "Rapporter er en del af platformen — jobs, tilbud, tid, lager og fakturering i ét samlet system. Alt taler sammen.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Styr hvert job", href: "/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Tilbud", sub: "Vind flere opgaver", href: "/features/offer" },
      { icon: "receipt-outline", iconBg: "var(--info-soft)", iconColor: "#2056C0", title: "Fakturaer", sub: "Få betaling til tiden", href: "/features" },
      { icon: "time-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Tid", sub: "Registrér timer", href: "/features/time-registration" },
      { icon: "people-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Kunder", sub: "Hele historikken", href: "/features/customer-database" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "Alle funktioner", sub: "Se hele platformen", href: "/features" },
    ],
    // security
    secLabel: "Sikkerhed hele vejen",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data i EU" },
      { icon: "chatbubbles-outline", text: "Support på dansk" },
    ],
    // FAQ
    faqH2: "Ofte stillede spørgsmål",
    faqs: [
      { q: "Erstatter Svendetid mit regneark?", a: "For de fleste håndværksvirksomheder erstatter Svendetid den daglige rapportering og de manuelle regneark. Tal fra jobs, tid og fakturaer samles automatisk, så du aldrig taster det samme to gange." },
      { q: "Hvor lang tid tager det at sætte op?", a: "Start fra en færdig skabelon og hav en brugbar rapport på få minutter. De fleste tilpasser layoutet på én arbejdsgang og deler et kundeklart overblik samme dag." },
      { q: "Kan jeg samle data fra hele forretningen?", a: "Alt der lever i Svendetid kan vises i rapporter — jobs, tid, tilbud, lager og fakturaer. Det hele opdateres i realtid, så tallene altid passer." },
      { q: "Hvad er gratis, og hvad koster penge?", a: "Du kan komme i gang gratis og prøve kerneoverblikket." },
    ],
    // CTA band
    bandH2: "Byg rapporter du faktisk kan arbejde ud fra",
    bandBody: "Slut dig til håndværkere der rapporterer og handler fra samme sted. Intet betalingskort. Gratis for altid.",
  },
  en: {
    eyebrow: "Reports in Svendetid",
    h1a: "Build reports you can actually ",
    h1b: "work",
    h1c: " from",
    sub: "See everything in one place — revenue, contribution margin per job and billable hours. And update the work straight from the report, without switching between programs.",
    cta: "Get started – it's free",
    seePricing: "See pricing",
    
    trust2: "Free to get started",
    trust3: "Danish support",
    mockReport: "Report",
    mockTitle: "Business overview",
    mockPeriod: "This month",
    mockShare: "Share",
    kpis: [
      { label: "Revenue", value: "kr 284,500", note: "▲ 12% vs. last mo.", noteColor: "var(--brand)" },
      { label: "Contribution margin", value: "38 %", note: "▲ 4 pct. points", noteColor: "var(--brand)" },
      { label: "Billable hours", value: "412 h", note: "86% of registered", noteColor: "var(--text-muted)" },
      { label: "Outstanding", value: "kr 47,200", note: "3 invoices overdue", noteColor: "var(--accent-strong)", valueColor: "var(--danger)" },
    ],
    chartTitle: "Revenue per week",
    chartUnit: "kr · 8 weeks",
    donutTitle: "Jobs per status",
    donutCenter: "jobs",
    statuses: [
      { label: "In progress", count: "10", color: "#2056C0" },
      { label: "Pending", count: "4", color: "#A57423" },
      { label: "To invoice", count: "5", color: "var(--accent)" },
      { label: "Done", count: "5", color: "var(--brand)" },
    ],
    recentTitle: "Recent jobs",
    seeAll: "See all",
    rows: [
      { initial: "N", bg: "var(--tint-peach)", color: "var(--accent-ink)", title: "Roof renovation", sub: "Nordby Bolig", status: "In progress", statusBg: "var(--tint-mint)", statusColor: "var(--brand)", amount: "kr 64,200" },
      { initial: "K", bg: "var(--tint-lilac)", color: "#4B3F8F", title: "Heat pump", sub: "Kruse family", status: "To invoice", statusBg: "var(--orange-100)", statusColor: "var(--accent-strong)", amount: "kr 38,900" },
      { initial: "L", bg: "var(--tint-rose)", color: "var(--danger)", title: "Bathroom renovation", sub: "Lund family", status: "Delayed", statusBg: "var(--tint-rose)", statusColor: "var(--danger)", amount: "kr 88,500" },
    ],
    cmpH2: "Reports you work from — not just look at",
    withoutLabel: "Without Svendetid",
    withLabel: "With Svendetid",
    without: [
      "Numbers in spreadsheets that never match reality",
      "Jumping between programs just to change one status",
      "Data scattered across notes, spreadsheets and apps",
      "Customers and the bookkeeper calling to hear where you stand",
    ],
    with: [
      "Always updated. Always in sync. No manual counting",
      "Change status, reassign or finish a job straight from the report",
      "All numbers in one place — no more exporting to spreadsheets",
      "Share one link instead of sending weekly status emails",
    ],
    introLabel: "How it works",
    introH2: "Reporting that works the way your crew does",
    introSub: "Action-oriented. Flexible. Shareable.",
    blocks: [
      {
        label: "Action-oriented",
        h3: "See the problem. Fix it. Same screen.",
        body: "Change status, reassign jobs and update fields straight in the report. When a job stalls, you fix it right away — no new tabs, no switching programs. The report is always in sync, because it is the work.",
        chips: [
          { icon: "swap-horizontal-outline", text: "Change status" },
          { icon: "people-outline", text: "Reassign" },
          { icon: "create-outline", text: "Update fields" },
        ],
        mock: "edit",
      },
      {
        label: "Flexible",
        h3: "One report per customer, project or crew",
        body: "Mix charts, tables, hours and calculations on one canvas. Drag, customise and arrange it to fit the way you work. Build crew reports, project overviews or customer portals — exactly the view each person needs.",
        chips: [
          { icon: "bar-chart-outline", text: "Charts" },
          { icon: "time-outline", text: "Hours" },
          { icon: "calculator-outline", text: "Calculations" },
        ],
        mock: "flexible",
      },
      {
        label: "Shareable",
        h3: "Share reports without giving access to everything",
        body: "Give customers, bookkeeper and partners a live overview with precise permissions. They only see what they should — nothing else. One link replaces every status email and PDF you've ever sent.",
        chips: [
          { icon: "lock-closed", text: "Permissions" },
          { icon: "link-outline", text: "One link" },
        ],
        mock: "share",
      },
    ],
    mockJobLabel: "Job · Roof renovation",
    mockStatus: "Status",
    mockResponsible: "Owner",
    mockAmount: "Amount",
    mockSaved: "Saved",
    mockFrom: "In progress",
    mockTo: "To invoice",
    mockHoursTitle: "Hours per crew · this week",
    mockMaterials: "Materials",
    mockMargin: "Margin",
    sharePortal: "Shared customer portal",
    shareLive: "Live",
    shareCustomer: "Anders (customer)",
    shareCustomerRole: "Can view",
    shareBookkeeper: "Bookkeeper",
    shareBookkeeperRole: "Finance only",
    shareHidden: "Internal prices & crew",
    shareHiddenRole: "Hidden",
    aiLabel: "AI-driven efficiency",
    aiH2a: "Svendetid AI turns data ",
    aiH2b: "into decisions",
    aiBody: "Ask anything: “Which jobs are at risk of running at a loss?” or “How many hours do we still need to invoice?”. The AI scans all your reports, spots patterns and explains it in language you can pass on.",
    aiCta: "Try Svendetid AI",
    aiQuestion: "Which jobs are at risk of running at a loss this month?",
    aiAnswerIntro: "2 jobs are below your target margin:",
    aiRow1: "Bathroom renovation · Lund family",
    aiRow1Val: "−6 %",
    aiRow2: "Carport · Holm Byg",
    aiRow2Val: "+3 %",
    aiAnswerOutro: "Material spend on Lund is 18% over the quote. Want to adjust?",
    gridH2: "Plus everything you need to run the business",
    grid: [
      { icon: "create-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Action-oriented reports", body: "Click into a card to reassign, update or finish a job. The report isn't just numbers — it's your command centre." },
      { icon: "people-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Overview of all crews", body: "See each crew's jobs, workload and status on one screen. No more calling around to hear where you stand." },
      { icon: "layers-outline", iconBg: "var(--info-soft)", iconColor: "#2056C0", title: "Ready-made templates", body: "Start with templates for project follow-up, customer reports or workload. Customise from there." },
      { icon: "funnel-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Advanced filtering", body: "Slice your numbers by owner, status, date or any field. Build exactly the view each person needs." },
      { icon: "share-social-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Shareable customer portals", body: "Replace PDFs with living reports. The customer sees progress. Your internal work stays private." },
      { icon: "pulse-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Live data in real time", body: "Always live. Always updated. Management sees real numbers — not last week's export." },
    ],
    platLabel: "The Svendetid platform",
    platH2: "Reports are only the beginning",
    platSub: "Reports are part of the platform — jobs, quotes, time, inventory and invoicing in one connected system. Everything talks to everything.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Run every job", href: "/en/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Quotes", sub: "Win more work", href: "/features/offer" },
      { icon: "receipt-outline", iconBg: "var(--info-soft)", iconColor: "#2056C0", title: "Invoices", sub: "Get paid on time", href: "/en/features" },
      { icon: "time-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Time", sub: "Register hours", href: "/en/features/time-registration" },
      { icon: "people-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Customers", sub: "The whole history", href: "/en/features/customer-database" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "All features", sub: "See the whole platform", href: "/en/features" },
    ],
    secLabel: "Security all the way",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data in the EU" },
      { icon: "chatbubbles-outline", text: "Support in Danish" },
    ],
    faqH2: "Frequently asked questions",
    faqs: [
      { q: "Does Svendetid replace my spreadsheet?", a: "For most trades businesses, Svendetid replaces the daily reporting and the manual spreadsheets. Numbers from jobs, time and invoices are gathered automatically, so you never type the same thing twice." },
      { q: "How long does it take to set up?", a: "Start from a ready-made template and have a usable report in minutes. Most people customise the layout in one sitting and share a customer-ready overview the same day." },
      { q: "Can I gather data from the whole business?", a: "Everything that lives in Svendetid can show up in reports — jobs, time, quotes, inventory and invoices. It all updates in real time, so the numbers always add up." },
      { q: "What's free and what costs money?", a: "You can get started free and try the core overview. Advanced reporting at scale and shared customer portals are part of the paid plans — upgrade when you're ready." },
    ],
    bandH2: "Build reports you can actually work from",
    bandBody: "Join trades businesses that report and act from the same place. No payment card. Free forever.",
  },
} as const;

type TT = (typeof T)["da"] | (typeof T)["en"];

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18,
  padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex", flexDirection: "column", gap: 15,
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em",
  textTransform: "uppercase", color: "var(--accent-strong)",
};

function SectionEyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 14 }}>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      <span style={monoLabel}>{label}</span>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

type Card = { icon: string; iconBg: string; iconColor: string; title: string; body: string };

function FeatureCard({ icon, iconBg, iconColor, title, body }: Card) {
  return (
    <div style={cardStyle}>
      <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={24} color={iconColor} />
      </div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{title}</h3>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{body}</p>
    </div>
  );
}

/* --------------------------- Feature-block mocks ------------------------- */
function EditMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 18 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 12 }}>{t.mockJobLabel}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--hairline)" }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)", width: 64 }}>{t.mockStatus}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#2056C0", background: "var(--info-soft)", borderRadius: 8, padding: "5px 10px" }}>{t.mockFrom}</span>
        <Icon name="arrow-forward" size={14} color="var(--text-faint)" />
        <span style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--accent)", borderRadius: 8, padding: "5px 10px", boxShadow: "0 6px 16px rgba(194,65,12,0.26)" }}>{t.mockTo}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--hairline)" }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)", width: 64 }}>{t.mockResponsible}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600 }}>
          <span style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--tint-mint)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-serif)", fontSize: 12, color: "var(--brand)" }}>M</span>Martin K.
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)", width: 64 }}>{t.mockAmount}</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 15, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>kr 64.200</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "var(--brand)", display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="checkmark-circle" size={14} color="var(--brand)" />{t.mockSaved}</span>
      </div>
    </div>
  );
}

function FlexibleMock({ t }: { t: TT }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <div style={{ gridColumn: "1 / -1", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 12px 28px rgba(16,33,27,0.1)", padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--green-900)", marginBottom: 10 }}>{t.mockHoursTitle}</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 64 }}>
          {["60%", "85%", "45%", "72%", "96%"].map((h, i) => (
            <div key={i} style={{ flex: 1, height: h, background: i === 1 ? "var(--brand)" : i === 4 ? "var(--accent)" : "var(--green-100)", borderRadius: "5px 5px 0 0" }} />
          ))}
        </div>
      </div>
      <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 12px 28px rgba(16,33,27,0.1)", padding: 16 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.mockMaterials}</div>
        <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 22, marginTop: 6 }}>kr 41.300</div>
      </div>
      <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 12px 28px rgba(16,33,27,0.1)", padding: 16 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.mockMargin}</div>
        <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 22, marginTop: 6, color: "var(--brand)" }}>+34 %</div>
      </div>
    </div>
  );
}

function ShareMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, borderBottom: "1px solid var(--hairline)" }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--orange-100)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="link-outline" size={18} color="var(--accent-strong)" /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t.sharePortal}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>svendetid.dk/del/nordby-bolig</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--brand)", background: "var(--tint-mint)", borderRadius: 8, padding: "4px 9px" }}>{t.shareLive}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--tint-teal)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-serif)", fontSize: 12, color: "#0E5E54" }}>A</span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{t.shareCustomer}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="eye-outline" size={14} />{t.shareCustomerRole}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--tint-lilac)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-serif)", fontSize: 12, color: "#4B3F8F" }}>B</span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{t.shareBookkeeper}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="receipt-outline" size={14} />{t.shareBookkeeperRole}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, opacity: 0.5 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--sage-100)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="lock-closed" size={13} color="var(--text-muted)" /></span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--text-muted)" }}>{t.shareHidden}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{t.shareHiddenRole}</span>
        </div>
      </div>
    </div>
  );
}

const MOCK_BG: Record<string, string> = {
  edit: "linear-gradient(155deg,var(--orange-100),#FFF7EF)",
  flexible: "linear-gradient(155deg,var(--green-50),#EEF6F0)",
  share: "linear-gradient(155deg,var(--tint-lilac),#F4F2FB)",
};

function FeatureBlock({ block, reverse, t }: { block: TT["blocks"][number]; reverse: boolean; t: TT }) {
  const text = (
    <div style={{ order: reverse ? 2 : 1 }}>
      <div style={{ ...monoLabel, marginBottom: 14 }}>{block.label}</div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 32, lineHeight: 1.12, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{block.h3}</h3>
      <p style={{ fontSize: 17, color: "var(--text-muted)", lineHeight: 1.6, margin: "18px 0 0" }}>{block.body}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
        {block.chips.map((c) => (
          <span key={c.text} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 999, padding: "7px 13px" }}>
            <Icon name={c.icon} size={15} color="var(--brand)" />{c.text}
          </span>
        ))}
      </div>
    </div>
  );
  const visual = (
    <div style={{ order: reverse ? 1 : 2, background: MOCK_BG[block.mock], borderRadius: 28, padding: "clamp(28px,5%,48px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {block.mock === "edit" && <EditMock t={t} />}
      {block.mock === "flexible" && <FlexibleMock t={t} />}
      {block.mock === "share" && <ShareMock t={t} />}
    </div>
  );
  return (
    <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
      <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
        {text}
        {visual}
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 16, padding: "20px 22px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "var(--font-sans)" }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: "var(--green-900)" }}>{q}</span>
        <svg viewBox="0 0 24 24" width={18} height={18} fill="none" style={{ color: "var(--accent-strong)", flex: "none", transition: "transform .25s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div style={{ padding: "0 22px 22px", fontSize: 15, lineHeight: 1.6, color: "var(--text-muted)" }}>{a}</div>}
    </div>
  );
}

export default function ReportsContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  const aiHref = lang === "en" ? "/en/ai" : "/ai";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`
        html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}
        @media (max-width: 720px){
          .svendetid-ds .rep-kpi{grid-template-columns:1fr 1fr !important;}
          .svendetid-ds .rep-charts{grid-template-columns:1fr !important;}
        }
      `}</style>

      <LandingNav lang={lang} accent="blue" />

      {/* ===== HERO ===== */}
      <section style={{ background: "#FFFFFF", color: "var(--text-body)", paddingTop: 64 }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "72px 32px 40px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, ...monoLabel, marginBottom: 22 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)" }} />{t.eyebrow}
          </div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 58, lineHeight: 1.03, letterSpacing: "-0.025em", margin: "0 auto", maxWidth: "14ch", color: "var(--green-900)", textWrap: "balance" }}>
            {t.h1a}<span style={{ fontStyle: "italic", color: "var(--brand)" }}>{t.h1b}</span>{t.h1c}
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.6, color: "var(--text-muted)", maxWidth: "60ch", margin: "24px auto 0", textWrap: "pretty" }}>{t.sub}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 34 }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-soft" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "var(--green-900)", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid var(--border)", background: "var(--surface-card)" }}>{t.seePricing}</Link>
          </div>
          <div style={{ display: "flex", gap: 22, justifyContent: "center", alignItems: "center", flexWrap: "wrap", marginTop: 20, fontSize: 13, color: "var(--text-muted)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust2}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust3}</span>
          </div>

          {/* HERO DASHBOARD IMAGE — Forretningsoverblik */}
          <div style={{ marginTop: 54, position: "relative" }}>
            {/* thick border fading from the page background (top) → yellow → dark green (bottom) */}
            <div
              style={{
                position: "relative",
                zIndex: 1,
                padding: 16,
                borderRadius: 14,
                background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 40%, #F0B429 72%, var(--green-900) 100%)",
              }}
            >
              <img
                src="/Report.png"
                alt={t.mockTitle}
                width={3687}
                height={1722}
                style={{ display: "block", width: "100%", height: "auto", borderRadius: 16 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== WITHOUT / WITH ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "60ch", margin: "0 auto 40px" }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.08, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.cmpH2}</h2>
        </div>
        <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 20, padding: 30 }}>
            <div style={{ ...monoLabel, color: "var(--text-muted)", marginBottom: 18 }}>{t.withoutLabel}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.without.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--tint-rose)", borderRadius: "50%", padding: 3, color: "var(--danger)" }}><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: "var(--green-900)", border: "1px solid #2C3B35", borderRadius: 20, padding: 30, boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, ...monoLabel, color: "var(--accent)", marginBottom: 18 }}>
              <img src="/svendetid-logo.png" alt="" style={{ width: 18, height: 18 }} />{t.withLabel}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.with.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--brand)", borderRadius: "50%", padding: 3, color: "#fff" }}><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-on-hero)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION INTRO ===== */}
      <section style={{ maxWidth: 760, margin: "0 auto", padding: "40px 32px 8px", textAlign: "center" }}>
        <SectionEyebrow label={t.introLabel} />
        <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.introH2}</h2>
        <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0", maxWidth: "48ch" }}>{t.introSub}</p>
      </section>

      {/* ===== THREE FEATURE BLOCKS ===== */}
      {t.blocks.map((block, i) => (
        <FeatureBlock key={block.label} block={block} reverse={i === 1} t={t} />
      ))}

      {/* ===== AI BLOCK ===== */}
      <section style={{ padding: "64px 32px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(32px,5vw,64px)", boxShadow: "0 24px 60px rgba(16,33,27,0.22)", position: "relative", overflow: "hidden" }}>
          <div aria-hidden style={{ position: "absolute", top: "-30%", right: "-10%", width: 420, height: 420, background: "radial-gradient(circle, rgba(249,115,22,0.22), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center", position: "relative" }}>
            <div>
              <div style={{ ...monoLabel, color: "var(--accent)", marginBottom: 16 }}>{t.aiLabel}</div>
              <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--text-on-hero)" }}>{t.aiH2a}<span style={{ fontStyle: "italic", color: "#F4C8A6" }}>{t.aiH2b}</span></h2>
              <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", margin: "20px 0 0", maxWidth: "46ch" }}>{t.aiBody}</p>
              <div style={{ marginTop: 28 }}>
                <Link href={aiHref} className="btn-blue" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.aiCta}<Icon name="arrow-forward" size={18} /></Link>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid #2C3B35", borderRadius: 18, padding: 18, backdropFilter: "blur(4px)" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: "var(--text-on-hero)", background: "var(--brand)", borderRadius: "14px 14px 4px 14px", padding: "10px 14px", maxWidth: "80%" }}>{t.aiQuestion}</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#FEC935,#FBA932)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="sparkles" size={15} color="#fff" /></span>
                <div style={{ background: "#151D1A", border: "1px solid #2C3B35", borderRadius: "4px 14px 14px 14px", padding: 14, fontSize: 13, color: "var(--text-on-hero)", lineHeight: 1.55 }}>
                  <div style={{ marginBottom: 10 }}>{t.aiAnswerIntro}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(160,66,51,0.16)", borderRadius: 8, marginBottom: 6 }}>
                    <span style={{ flex: 1 }}>{t.aiRow1}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#E0907F" }}>{t.aiRow1Val}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(165,116,35,0.16)", borderRadius: 8 }}>
                    <span style={{ flex: 1 }}>{t.aiRow2}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#E3B85F" }}>{t.aiRow2Val}</span>
                  </div>
                  <div style={{ marginTop: 10, color: "var(--text-hero-muted)" }}>{t.aiAnswerOutro}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURE GRID ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.gridH2}</h2>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {t.grid.map((f) => <FeatureCard key={f.title} {...f} />)}
        </div>
      </section>

      {/* ===== PLATFORM ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "52ch", margin: "0 auto 36px" }}>
          <div style={{ ...monoLabel, marginBottom: 14 }}>{t.platLabel}</div>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.platH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0" }}>{t.platSub}</p>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {t.platform.map((p) => (
            <Link key={p.title} href={p.href} className="card-link" style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 16, textDecoration: "none", color: "inherit" }}>
              <span style={{ width: 38, height: 38, borderRadius: 10, background: p.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name={p.icon} size={19} color={p.iconColor} /></span>
              <span>
                <span style={{ display: "block", fontSize: 14, fontWeight: 600 }}>{p.title}</span>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{p.sub}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== SECURITY ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 32px 8px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "18px 40px", padding: "28px 24px", background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 18 }}>
          <span style={{ ...monoLabel, color: "var(--text-muted)" }}>{t.secLabel}</span>
          {t.security.map((s) => (
            <span key={s.text} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "var(--green-900)" }}><Icon name={s.icon} size={18} color="var(--brand)" />{s.text}</span>
          ))}
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section style={{ maxWidth: 780, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.faqH2}</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {t.faqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 32px 72px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(40px,6vw,72px) 32px", textAlign: "center", position: "relative", overflow: "hidden", boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
          <div aria-hidden style={{ position: "absolute", bottom: "-40%", left: "50%", transform: "translateX(-50%)", width: 600, height: 400, background: "radial-gradient(circle, rgba(249,115,22,0.2), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <img src="/svendetid-logo.png" alt="" style={{ width: 48, height: 48, margin: "0 auto 22px", display: "block", position: "relative" }} />
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 44, lineHeight: 1.08, letterSpacing: "-0.02em", margin: "0 auto", maxWidth: "18ch", color: "#fff", position: "relative", textWrap: "balance" }}>{t.bandH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-hero-muted)", margin: "18px auto 0", maxWidth: "46ch", position: "relative" }}>{t.bandBody}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 30, position: "relative" }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/features/reports" enHref="/en/features/reports" />
    </div>
  );
}
